import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {forkJoin, Observable, of} from 'rxjs';
import {UserInfoStorageService} from './user-info-storage.service';
import {CrystalClearFrontUrl} from '../../shared/pages/crystal-clear-front-url';
import {SessionNames} from "../../shared/session/session-names";
import {CookieNames} from "../../shared/cookie/cookie-names";
import {CcStorageService} from "../common/cc-storage.service";
import {catchError, map} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {AppConfigService} from "../app-config.service";

@Injectable({providedIn: 'root'})
export class IsLoggedPageGuard implements CanActivate {
  constructor(protected router: Router, protected authService: UserInfoStorageService,
              protected cookieService: CcStorageService, protected appConfigService: AppConfigService) {
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    let url: string = state.url;
    return this.checkUserLogin(next, url);
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivate(next, state);
  }

  canDeactivate(
    component: unknown,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return true;
  }

  canLoad(
    route: Route,
    segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    return true;
  }

  checkUserLogin(route: ActivatedRouteSnapshot, url: string): Observable<boolean | UrlTree> {
    if (!this.authService.isAuthenticated) {
      return of(this.router.parseUrl(CrystalClearFrontUrl.LOGIN.path));
    }
    return forkJoin([
      this.authService.refreshConnectedUserCacheInfo(),
      // Populates AppConfigService's authenticated-only fields (see loadAuthenticated()) -
      // needed on both "just logged in" and "page refresh while still logged in", which is
      // exactly when this guard runs. Cached after the first successful call.
      this.appConfigService.loadAuthenticated()
    ]).pipe(
      map(([user]) => {

        const userRoles = JSON.parse(sessionStorage.getItem(SessionNames.USER_ROLES) || '[]') as string[];
        const requiredRoles = route.data.role || [];

        const authorized = requiredRoles.length == 0 || userRoles.some(userRole => requiredRoles.includes(userRole));

        if (!authorized) {
          return this.router.parseUrl(CrystalClearFrontUrl.HOME.path);
        }

        this.cookieService.set(CookieNames.PAGE_BEFORE_LOGOUT, url);
        return true;
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
          return of(this.router.parseUrl(CrystalClearFrontUrl.LOGIN.path));
        }
        return of(true);
      })
    );
  }

}
